import React from "react"
import Card from "../../../containers/Card/Card"
import DepositProductField from "./DepositProductField/DepositProductField"
import {DepositProductInterestProps} from "./types"
import {EditMode} from "../../../resources/common"
import {toInteger} from "lodash"
import FieldViewBox from "../../../components/Interest/FieldViewBox"
import FormulaTag from "../../../components/Interest/FormulaTag"
import type {CustomerInterestTier} from "../../../resources/depositProduct"
import {isBasisPoints, getTierRateDisplay, getTierRangeDisplay} from "../../../components/Interest/interestUtils"

export default function Interest({editMode, values, setFieldValue, onBlur}: DepositProductInterestProps) {
	const hasTieredInterest = values.enableTieredCustomerInterest === true
	const hasCustomerFormula = values.enableCustomerInterestFormula === true
	const tiers: CustomerInterestTier[] = values.customerInterestTiers ?? []

	if (editMode === EditMode.EDIT) {
		return (
			<Card
				className="deposit-product-card-title"
				title={
					<span className="card-title" id="Interest">
						Interest
					</span>
				}
			>
				<div className="form-row four">
					<DepositProductField
						mode={editMode}
						type="select"
						name="OrgInterestShare"
						label={"Org Interest Share"}
						value={values.orgInterestShare ?? "-"}
						onChange={(value) => setFieldValue("OrgInterestShare", toInteger(value))}
						inputSuffix={values.orgInterestShare !== undefined ? "%" : ""}
						onBlur={onBlur}
					/>
					<DepositProductField
						mode={editMode}
						type="select"
						name="CustomerInterestShare"
						label={"Customer Interest Share"}
						value={values.customerInterestShare ?? "-"}
						onChange={(value) => setFieldValue("CustomerInterestShare", toInteger(value))}
						inputSuffix={values.customerInterestShare !== undefined ? "%" : ""}
						onBlur={onBlur}
					/>
					<DepositProductField
						mode={editMode}
						type="select"
						name="InterestFormula"
						label={"Interest Formula"}
						value={values.interestFormula ?? "-"}
						onChange={(value) => setFieldValue("InterestFormula", value)}
						onBlur={onBlur}
					/>
					<DepositProductField
						mode={editMode}
						type="select"
						name="InterestRate"
						label={"Interest Rate"}
						value={values.interestRate ?? "-"}
						onChange={(value) => setFieldValue("InterestRate", value)}
						onBlur={onBlur}
					/>
				</div>
			</Card>
		)
	}

	return (
		<Card
			className="deposit-product-card-title"
			title={
				<span className="card-title" id="Interest">
					Interest
				</span>
			}
		>
			{/* Row 1: Interest Formula + Interest Rate (always visible in VIEW) */}
			<div className="form-row four view">
				<FieldViewBox label="Interest Formula">
					{values.interestFormula ? (
						<FormulaTag formula={values.interestFormula} />
					) : (
						<span className="value-text not-set">Not set</span>
					)}
				</FieldViewBox>
				<FieldViewBox label="Interest Rate">
					<span className="value-text">{values.interestRate ?? "-"}%</span>
				</FieldViewBox>
			</div>

			{/* Mode-specific content */}
			{hasTieredInterest && tiers.length > 0 ? (
				<div className="tiered-interest-section">
					<span className="sub-title">Tiered Interest</span>
					<div className="form-row four view">
						<FieldViewBox label="Calculation Method">
							<span className="value-text">{values.customerInterestTieringMode ?? "Blended"}</span>
						</FieldViewBox>
					</div>
					<div className="form-row four view tier-grid">
						{tiers.map((tier: CustomerInterestTier, index: number) => {
							const formula = tier.formula.trim()
							const isFormula = !isBasisPoints(formula)
							const rateDisplay = getTierRateDisplay(tier)
							const rangeDisplay = getTierRangeDisplay(tier)

							return (
								<FieldViewBox key={index} label={`Tier ${index + 1}`} className="tier-box">
									<span className="tier-range-text">{rangeDisplay}</span>
									{isFormula ? (
										<FormulaTag formula={formula} maxWidth="180px" />
									) : (
										rateDisplay && <span className="value-text">{rateDisplay}</span>
									)}
								</FieldViewBox>
							)
						})}
					</div>
				</div>
			) : hasCustomerFormula ? (
				<div className="form-row four view">
					<FieldViewBox label="Calculation Method">
						<span className="value-text">Customer Interest Formula</span>
					</FieldViewBox>
					<FieldViewBox label="Formula">
						{values.customerInterestFormula ? (
							<FormulaTag formula={values.customerInterestFormula} />
						) : (
							<span className="value-text not-set">Not set</span>
						)}
					</FieldViewBox>
				</div>
			) : (
				<div className="form-row four view">
					<FieldViewBox label="Org Interest Share">
						<span className="value-text">
							{values.orgInterestShare !== undefined ? `${values.orgInterestShare}%` : "Not set"}
						</span>
					</FieldViewBox>
					<FieldViewBox label="Customer Interest Share">
						<span className="value-text">
							{values.customerInterestShare !== undefined ? `${values.customerInterestShare}%` : "Not set"}
						</span>
					</FieldViewBox>
				</div>
			)}
		</Card>
	)
}
